import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import { Button } from '@fiado/react-kit';
import { cn } from '@/lib/cn';

interface Props {
    /** El token en claro. `null` ⇒ el modal está cerrado. */
    token: string | null;
    /** Se llama cuando el operador confirma que ya lo guardó. El padre debe poner `token` en null. */
    onConfirm: () => void;
}

/**
 * Muestra el secreto de una llave recién emitida y no deja salir sin confirmar.
 * El porqué de no usar el `<Modal>` del kit está en DEC-RBAC-120.
 */
export function IssuedSecretModal({ token, onConfirm }: Props) {
    const [confirmed, setConfirmed] = useState(false);
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(token ?? '');
            setCopied(true);
        } catch {
            // Sin clipboard el token sigue visible y seleccionable: no se vuelve error bloqueante.
            setCopied(false);
        }
    };

    return (
        <DialogPrimitive.Root open={token !== null}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm" />
                <DialogPrimitive.Content
                    className="fixed left-[50%] top-[50%] z-50 grid max-h-[90vh] w-full max-w-xl translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto rounded-2xl border border-border bg-card p-6 shadow-2xl"
                    // Las tres salidas accidentales, cerradas: el secreto no se puede recuperar.
                    onEscapeKeyDown={(e) => {
                        e.preventDefault();
                    }}
                    onInteractOutside={(e) => {
                        e.preventDefault();
                    }}
                    onPointerDownOutside={(e) => {
                        e.preventDefault();
                    }}
                >
                    <DialogPrimitive.Title className="flex items-center gap-2 text-lg font-semibold">
                        <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
                        Guarda esta llave ahora
                    </DialogPrimitive.Title>

                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                        Es la única vez que vas a ver este valor. En la base solo queda su huella,
                        así que nadie —ni soporte, ni nosotros— puede recuperarlo después. Si lo
                        pierdes, tienes que revocar esta llave y emitir otra.
                    </DialogPrimitive.Description>

                    <div className="rounded-lg border border-border bg-muted/40 p-3">
                        {/* `sentry-mask` impide que el replay de sesión capture el secreto. */}
                        <code
                            data-testid="plaintext-secret"
                            className="sentry-mask block break-all font-mono text-sm"
                        >
                            {token}
                        </code>
                    </div>

                    <Button variant="outline" onClick={() => void copy()} className="w-fit">
                        {copied ? (
                            <>
                                <Check className="mr-2 h-4 w-4" aria-hidden /> Copiado
                            </>
                        ) : (
                            <>
                                <Copy className="mr-2 h-4 w-4" aria-hidden /> Copiar llave
                            </>
                        )}
                    </Button>
                    {/* El cambio de texto del botón no lo anuncia un lector de pantalla. */}
                    <span aria-live="polite" className="sr-only">
                        {copied ? 'Llave copiada al portapapeles' : ''}
                    </span>

                    <label className="flex cursor-pointer items-start gap-2 text-sm">
                        <input
                            type="checkbox"
                            className={cn('mt-0.5 h-4 w-4 rounded border-border')}
                            checked={confirmed}
                            onChange={(e) => {
                                setConfirmed(e.target.checked);
                            }}
                        />
                        <span>Ya guardé la llave en un lugar seguro.</span>
                    </label>

                    <div className="flex justify-end">
                        <Button onClick={onConfirm} disabled={!confirmed}>
                            Listo, ya la guardé
                        </Button>
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
